import React, { useEffect, useState } from "react";
import FormControl from "@carrier-io/air-react/FormControl";
import Select from "@carrier-io/air-react/Select";
import Input from "@carrier-io/air-react/Input";
import { SelectArrowIcon } from "../userManagement/Icons/SelectArrowIcon";
import MenuItem from "@carrier-io/air-react/MenuItem";
import FormHelperText from "@carrier-io/air-react/FormHelperText";
import { SelectChangeEvent } from "@mui/material";
import { GetAllRolesQuery } from "../../graphql/queries/getAllRoles";
import { Clients, useQuery } from "../../apollo";
import { Skeleton } from "@carrier-io/air-react";
import { GetAllGroupsQuery } from "../../graphql/queries/getAllGroups";
import { SelectArrow } from "../selectArrow/SelectArrow";
import { UIUserStatus } from "../../types/user";

type SearchUsersParamsType = {
  role: string;
  group: string;
  status: UIUserStatus;
  onRoleChange: (value: string) => void;
  onGroupChange: (value: string) => void;
  onStatusChange: (value: UIUserStatus) => void;
};

export const SearchUsersParams = ({
  role: predefinedRole,
  group: predefinedGroup,
  status: predefinedStatus,
  onRoleChange,
  onGroupChange,
  onStatusChange,
}: SearchUsersParamsType) => {
  const [role, setRole] = useState("");
  const [group, setGroup] = useState("");
  const [status, setStatus] = useState("");

  const { data: roles, loading: isRolesLoading } = useQuery<{
    getAllRoles: Array<string>;
  }>(Clients.UPS, GetAllRolesQuery);

  const { data: groups, loading: isGroupsLoading } = useQuery<{
    getAllGroups: Array<string>;
  }>(Clients.UPS, GetAllGroupsQuery);

  const handleChangeRole = (event: SelectChangeEvent<unknown>) => {
    const target = event.target.value as string;
    setRole(target);
    onRoleChange(target);
  };

  const handleChangeGroup = (event: SelectChangeEvent<unknown>) => {
    const target = event.target.value as string;
    setGroup(target);
    onGroupChange(target);
  };

  const handleChangeStatus = (event: SelectChangeEvent<unknown>) => {
    const target = event.target.value as UIUserStatus;
    setStatus(target);
    onStatusChange(target);
  };

  useEffect(() => {
    setRole(predefinedRole);
    setGroup(predefinedGroup);
    setStatus(predefinedStatus);
  }, [predefinedGroup, predefinedRole, predefinedStatus]);

  return (
    <>
      {isGroupsLoading ? (
        <Skeleton
          data-testid="skeleton"
          height={32}
          width={90}
          sx={{ transform: "scale(1)" }}
        />
      ) : (
        <FormControl
          sx={{
            maxWidth: 90,
            width: "100%",
          }}
          variant="filled"
        >
          <Select
            data-testid="select-group"
            hideBackgroundColor
            input={
              <Input
                hideBackgroundColor
                showBorder
                hideLabel
                sx={{ minWidth: 90, width: "100%" }}
              />
            }
            onChange={handleChangeGroup}
            showBorder
            IconComponent={SelectArrow}
            displayEmpty
            value={group || ""}
            renderValue={(value) =>
              value === "" ? "Group" : (value as React.ReactNode)
            }
            classes={{ select: "red-hat-text" }}
            size="small"
            sx={{
              fontSize: "14px",
              marginRight: 1,
              minWidth: 90,
              width: "100%",
            }}
          >
            {groups?.getAllGroups.map((group) => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText />
        </FormControl>
      )}
      {isRolesLoading ? (
        <Skeleton height={32} width={75} sx={{ transform: "scale(1)" }} />
      ) : (
        <FormControl
          sx={{
            maxWidth: 75,
            width: "100%",
          }}
          variant="filled"
        >
          <Select
            data-testid="select-role"
            hideBackgroundColor
            input={
              <Input
                hideBackgroundColor
                showBorder
                hideLabel
                sx={{ minWidth: 75, width: "100%" }}
              />
            }
            onChange={handleChangeRole}
            showBorder
            IconComponent={({ className }) => (
              <SelectArrowIcon className={className} />
            )}
            displayEmpty
            value={role || ""}
            renderValue={(value) =>
              value === "" ? "Role" : (value as React.ReactNode)
            }
            classes={{ select: "red-hat-text" }}
            sx={{
              fontSize: "14px",
              marginRight: 1,
              minWidth: 75,
              width: "100%",
              maxHeight: 32,
            }}
          >
            {roles?.getAllRoles.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText />
        </FormControl>
      )}
      <FormControl
        sx={{
          maxWidth: 90,
          width: "100%",
        }}
        variant="filled"
      >
        <Select
          data-testid="select-status"
          hideBackgroundColor
          input={
            <Input
              hideBackgroundColor
              showBorder
              hideLabel
              sx={{ minWidth: 75, width: "100%" }}
            />
          }
          onChange={handleChangeStatus}
          showBorder
          IconComponent={SelectArrow}
          displayEmpty
          value={status || ""}
          renderValue={(value) =>
            value === "" ? "Status" : (value as React.ReactNode)
          }
          classes={{ select: "red-hat-text" }}
          size="small"
          sx={{
            fontSize: "14px",
            marginRight: 1,
            minWidth: 100,
            width: "100%",
          }}
        >
          {Object.values(UIUserStatus).map((statusOption) => (
            <MenuItem key={statusOption} value={statusOption}>
              {statusOption}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText />
      </FormControl>
    </>
  );
};
